import { clientOrigin, marketingOrigin } from '../common/siteEnvironment';
import React, { useState, useEffect } from 'react';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { signInWithMfa } from './mfaDialogs';
import { MFA_LOGIN_NOTICE } from '../common/mfa';

interface ClientGatewayProps {
  onLogin: (email: string, password: string) => Promise<void>;
}

const SUITES = [
  { icon: '🛡️', name: 'Security Risk Management', desc: 'Threat matrices, risk registers, FSAT assessments and SRM frameworks for enterprise security operations.' },
  { icon: '🚨', name: 'Emergency Preparedness', desc: 'Executive protection, advance site surveys, route analysis and EP operations planning for high-risk environments.' },
  { icon: '🏗️', name: 'Enterprise Resiliency', desc: 'Business continuity planning, crisis response frameworks and operational resilience assessment.' },
  { icon: '⚡', name: 'Crisis Management', desc: 'Real-time crisis command protocols, stakeholder communications and incident response playbooks.' },
  { icon: '✅', name: 'HSSE Compliance', desc: 'Health, Safety, Security and Environment auditing and regulatory framework management worldwide.' },
  { icon: '🌐', name: 'Global Intelligence Hub', desc: 'Country risk profiles, geopolitical deep dives, live threat feeds and PMESII/ASCOPE assessments.' },
];

const STEPS = [
  { num: '01', title: 'Sign In to Your Account', desc: 'Use the email and password provided during your onboarding. If you received a team invitation, enter your email and sign in directly.' },
  { num: '02', title: 'Access Your Dashboard', desc: 'Your Organisation Dashboard gives you access to delivered reports, global risks, assets, learning, and consultant support.' },
  { num: '03', title: 'Review Your Intelligence', desc: 'Open reports and completed work prepared by your IntelOptics consultant, then download anything you need.' },
];

const ClientGateway: React.FC<ClientGatewayProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(() => sessionStorage.getItem(MFA_LOGIN_NOTICE));
  const [showReset, setShowReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const auth = getAuth();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setInfo(null);
    try {
      sessionStorage.removeItem(MFA_LOGIN_NOTICE);
      await signInWithMfa(email, password);
      await onLogin(email, password);
    } catch (err: any) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
        setError('Incorrect email or password. Please try again or use Forgot Password.');
      } else {
        setError(err.message || 'An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email address first.'); return; }
    setIsLoading(true);
    try {
      await sendPasswordResetEmail(auth, email, { url: window.location.origin, handleCodeInApp: false });
      setResetSent(true);
      setError(null);
    } catch (err: any) {
      setError('Could not send reset email. Please check the email address and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      fontFamily: 'Inter, -apple-system, sans-serif',
      background: '#0f172a',
      color: '#f8fafc',
      minHeight: '100vh',
      overflowX: 'hidden',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        :root { --blue: #3b82f6; --teal: #06b6d4; --navy: #0f172a; --navy2: #1e293b; --border: rgba(56,130,246,0.18); --grey: #94a3b8; --lgrey: #64748b; }
        .cgw-body::before { content:''; position:fixed; inset:0; background-image: linear-gradient(rgba(56,130,246,0.045) 1px,transparent 1px), linear-gradient(90deg,rgba(56,130,246,0.045) 1px,transparent 1px); background-size:56px 56px; pointer-events:none; z-index:0; animation:cgwGrid 20s linear infinite; }
        @keyframes cgwGrid { from{background-position:0 0} to{background-position:56px 56px} }
        .cgw-nav { position:fixed; top:0; left:0; right:0; z-index:100; height:68px; display:flex; align-items:center; justify-content:space-between; padding:0 2.5rem; background:rgba(10,17,32,0.85); backdrop-filter:blur(20px); border-bottom:1px solid var(--border); }
        .cgw-logo { display:flex; align-items:center; gap:12px; text-decoration:none; }
        .cgw-logo-icon { width:42px; height:42px; border-radius:50%; overflow:hidden; box-shadow:0 0 0 1.5px rgba(56,130,246,0.3), 0 0 14px rgba(56,130,246,0.2); flex-shrink:0; }
        .cgw-logo-icon img { width:100%; height:100%; object-fit:cover; display:block; }
        .cgw-logo-text { font-size:1.05rem; font-weight:900; letter-spacing:-.01em; color:#f8fafc; line-height:1; }
        .cgw-logo-sub { font-size:.6rem; font-weight:700; color:var(--teal); letter-spacing:.1em; text-transform:uppercase; margin-top:3px; }
        .cgw-nav-right { display:flex; align-items:center; gap:1.25rem; }
        .cgw-nav-link { font-size:.8rem; font-weight:600; color:var(--grey); text-decoration:none; transition:color .2s; }
        .cgw-nav-link:hover { color:#fff; }
        .cgw-nav-badge { display:inline-flex; align-items:center; gap:6px; padding:.3rem .75rem; background:rgba(56,130,246,.1); border:1px solid rgba(56,130,246,.25); border-radius:999px; font-size:.72rem; font-weight:700; color:var(--blue); }
        .cgw-live-dot { width:6px; height:6px; background:var(--teal); border-radius:50%; animation:cgwPulse 2s infinite; }
        @keyframes cgwPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(.7)} }
        .cgw-hero { min-height:100vh; display:grid; grid-template-columns:1fr 420px; gap:4rem; align-items:center; padding:8rem 2.5rem 4rem; max-width:1240px; margin:0 auto; position:relative; z-index:1; }
        .cgw-hero-badge { display:inline-flex; align-items:center; gap:7px; background:rgba(56,130,246,.08); border:1px solid rgba(56,130,246,.25); padding:6px 16px; border-radius:999px; font-size:.72rem; font-weight:700; color:var(--blue); text-transform:uppercase; letter-spacing:.08em; margin-bottom:1.75rem; }
        .cgw-hero-title { font-size:clamp(2.2rem,4.5vw,3.4rem); font-weight:900; line-height:1.08; letter-spacing:-.03em; margin-bottom:1.25rem; }
        .cgw-hero-grad { background:linear-gradient(135deg,var(--blue),var(--teal)); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
        .cgw-hero-sub { font-size:.97rem; color:var(--grey); line-height:1.8; margin-bottom:2rem; max-width:540px; }
        .cgw-feature-pills { display:flex; flex-wrap:wrap; gap:.6rem; margin-bottom:2.25rem; }
        .cgw-pill { display:inline-flex; align-items:center; gap:.4rem; padding:.4rem .9rem; background:rgba(30,41,59,0.7); border:1px solid var(--border); border-radius:999px; font-size:.75rem; font-weight:600; color:var(--grey); }
        .cgw-pill-dot { width:6px; height:6px; border-radius:50%; background:linear-gradient(135deg,var(--blue),var(--teal)); flex-shrink:0; }
        .cgw-eprims-link { display:inline-flex; align-items:center; gap:.5rem; font-size:.82rem; color:var(--lgrey); text-decoration:none; padding:.5rem 1rem; border:1px solid var(--border); border-radius:8px; transition:all .2s; }
        .cgw-eprims-link:hover { border-color:var(--blue); color:var(--blue); }
        .cgw-card { background:rgba(15,23,42,0.92); border:1px solid var(--border); border-radius:20px; padding:2.25rem; backdrop-filter:blur(16px); box-shadow:0 24px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(56,130,246,0.08); position:sticky; top:88px; }
        .cgw-card-header { text-align:center; margin-bottom:1.75rem; }
        .cgw-card-logo { width:64px; height:64px; border-radius:50%; overflow:hidden; margin:0 auto .85rem; box-shadow:0 0 0 2px rgba(56,130,246,0.25), 0 0 20px rgba(56,130,246,0.2); }
        .cgw-card-logo img { width:100%; height:100%; object-fit:cover; }
        .cgw-card-title { font-size:1.1rem; font-weight:800; color:#fff; margin-bottom:.25rem; }
        .cgw-card-sub { font-size:.8rem; color:var(--grey); }
        .cgw-tabs { display:grid; grid-template-columns:1fr 1fr; background:rgba(30,41,59,0.6); border:1px solid var(--border); border-radius:10px; padding:3px; margin-bottom:1.5rem; }
        .cgw-tab { padding:.55rem; text-align:center; font-size:.8rem; font-weight:700; border-radius:8px; cursor:pointer; transition:all .2s; color:var(--lgrey); border:none; background:none; }
        .cgw-tab.active { background:linear-gradient(135deg,var(--blue),#1d4ed8); color:#fff; box-shadow:0 2px 8px rgba(56,130,246,0.3); }
        .cgw-form-group { margin-bottom:1rem; }
        .cgw-label { display:block; font-size:.7rem; font-weight:700; color:var(--lgrey); margin-bottom:.4rem; text-transform:uppercase; letter-spacing:.07em; }
        .cgw-input { width:100%; background:rgba(10,17,32,0.8); border:1px solid rgba(56,130,246,.2); border-radius:9px; padding:.7rem 1rem; color:#fff; font-size:.9rem; font-family:Inter,sans-serif; transition:border-color .2s; outline:none; box-sizing:border-box; }
        .cgw-input:focus { border-color:var(--blue); background:rgba(15,23,42,.95); }
        .cgw-input::placeholder { color:var(--lgrey); }
        .cgw-btn { width:100%; padding:.85rem; background:linear-gradient(135deg,var(--blue),#1d4ed8); color:#fff; font-size:.95rem; font-weight:700; border:none; border-radius:10px; cursor:pointer; transition:all .2s; font-family:Inter,sans-serif; box-shadow:0 4px 16px rgba(56,130,246,0.3); display:flex; align-items:center; justify-content:center; gap:.5rem; }
        .cgw-btn:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 24px rgba(56,130,246,0.45); }
        .cgw-btn:disabled { opacity:.6; cursor:not-allowed; transform:none; }
        .cgw-alert { padding:.75rem 1rem; border-radius:9px; font-size:.82rem; line-height:1.6; margin-bottom:1rem; }
        .cgw-alert-err { background:rgba(239,68,68,.12); border:1px solid rgba(239,68,68,.3); color:#fca5a5; }
        .cgw-alert-info { background:rgba(56,130,246,.12); border:1px solid rgba(56,130,246,.3); color:#93c5fd; }
        .cgw-forgot { font-size:.75rem; color:var(--lgrey); text-align:right; margin-top:.25rem; margin-bottom:.25rem; }
        .cgw-forgot button { background:none; border:none; color:var(--blue); cursor:pointer; font-size:.75rem; font-weight:600; }
        .cgw-forgot button:hover { color:var(--teal); }
        .cgw-divider { display:flex; align-items:center; gap:.75rem; margin:1rem 0; }
        .cgw-divider-line { flex:1; height:1px; background:var(--border); }
        .cgw-divider span { font-size:.72rem; color:var(--lgrey); font-weight:600; }
        .cgw-card-footer { margin-top:1.25rem; padding-top:1rem; border-top:1px solid var(--border); text-align:center; font-size:.72rem; color:var(--lgrey); line-height:1.6; }
        .cgw-card-footer a { color:var(--blue); text-decoration:none; }
        .cgw-section { position:relative; z-index:1; padding:5rem 2.5rem; }
        .cgw-section-inner { max-width:1200px; margin:0 auto; }
        .cgw-sec-label { font-size:.7rem; font-weight:700; letter-spacing:.14em; text-transform:uppercase; color:var(--blue); margin-bottom:.75rem; display:inline-flex; align-items:center; gap:.5rem; }
        .cgw-sec-label::before { content:''; width:18px; height:1.5px; background:var(--blue); border-radius:1px; }
        .cgw-sec-title { font-size:clamp(1.7rem,3vw,2.4rem); font-weight:800; line-height:1.12; letter-spacing:-.02em; margin-bottom:.85rem; }
        .cgw-sec-sub { font-size:.95rem; color:var(--grey); line-height:1.8; max-width:580px; }
        .cgw-suites-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(320px,1fr)); gap:1.1rem; margin-top:2.5rem; }
        .cgw-suite { background:rgba(30,41,59,0.55); border:1px solid var(--border); border-radius:16px; padding:1.75rem; transition:all .25s; position:relative; overflow:hidden; }
        .cgw-suite::before { content:''; position:absolute; top:0; left:0; right:0; height:2px; background:linear-gradient(90deg,var(--blue),var(--teal)); transform:scaleX(0); transform-origin:left; transition:transform .3s; }
        .cgw-suite:hover::before { transform:scaleX(1); }
        .cgw-suite:hover { border-color:rgba(56,130,246,.4); transform:translateY(-3px); }
        .cgw-suite-icon { font-size:1.5rem; margin-bottom:1rem; }
        .cgw-suite-name { font-size:.95rem; font-weight:800; color:#fff; margin-bottom:.4rem; }
        .cgw-suite-desc { font-size:.84rem; color:var(--grey); line-height:1.7; }
        .cgw-steps { display:grid; grid-template-columns:repeat(auto-fit,minmax(280px,1fr)); gap:1.5rem; margin-top:2.5rem; }
        .cgw-step { background:rgba(30,41,59,0.4); border:1px solid var(--border); border-radius:16px; padding:1.75rem; }
        .cgw-step-num { font-size:.75rem; font-weight:900; color:var(--blue); letter-spacing:.1em; margin-bottom:.6rem; }
        .cgw-step-title { font-size:1rem; font-weight:800; color:#fff; margin-bottom:.5rem; }
        .cgw-step-desc { font-size:.84rem; color:var(--grey); line-height:1.7; }
        .cgw-support-grid { display:grid; grid-template-columns:1fr 1fr; gap:2rem; margin-top:2.5rem; }
        .cgw-support-card { background:rgba(6,182,212,.06); border:1px solid rgba(6,182,212,.2); border-radius:16px; padding:1.75rem; }
        .cgw-support-title { font-size:1rem; font-weight:800; color:#fff; margin-bottom:.75rem; display:flex; align-items:center; gap:.6rem; }
        .cgw-support-body { font-size:.87rem; color:var(--grey); line-height:1.8; }
        .cgw-support-body a { color:var(--blue); text-decoration:none; }
        .cgw-support-body strong { color:#fff; }
        .cgw-divider-full { border:none; border-top:1px solid var(--border); margin:0; position:relative; z-index:1; }
        .cgw-footer { background:rgba(5,10,22,0.95); border-top:1px solid var(--border); padding:2rem 2.5rem; position:relative; z-index:1; }
        .cgw-footer-inner { max-width:1200px; margin:0 auto; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:1rem; }
        .cgw-footer-left { font-size:.82rem; color:var(--lgrey); }
        .cgw-footer-links { display:flex; gap:1.5rem; flex-wrap:wrap; }
        .cgw-footer-links a { color:var(--lgrey); text-decoration:none; font-size:.8rem; transition:color .2s; }
        .cgw-footer-links a:hover { color:var(--blue); }
        .cgw-spinner { width:18px; height:18px; border:2px solid rgba(255,255,255,0.3); border-top-color:#fff; border-radius:50%; animation:cgwSpin .7s linear infinite; }
        @keyframes cgwSpin { to{transform:rotate(360deg)} }
        @media(max-width:1024px) { .cgw-hero { grid-template-columns:1fr; min-height:auto; padding-top:7rem; } .cgw-card { position:static; } }
        @media(max-width:768px) { .cgw-nav { padding:0 1.25rem; } .cgw-hero { padding:6rem 1.25rem 3rem; gap:2.5rem; } .cgw-section { padding:3.5rem 1.25rem; } .cgw-support-grid { grid-template-columns:1fr; } }
      `}</style>

      {/* Body grid background */}
      <div className="cgw-body" style={{ position: 'fixed', inset: 0, backgroundImage: 'linear-gradient(rgba(56,130,246,0.045) 1px,transparent 1px),linear-gradient(90deg,rgba(56,130,246,0.045) 1px,transparent 1px)', backgroundSize: '56px 56px', pointerEvents: 'none', zIndex: 0 }} />

      {/* Ambient glow */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, background: 'radial-gradient(ellipse 700px 500px at 15% 30%,rgba(59,130,246,0.07) 0%,transparent 60%),radial-gradient(ellipse 500px 400px at 85% 75%,rgba(6,182,212,0.06) 0%,transparent 60%)' }} />

      {/* ── Navigation ── */}
      <nav className="cgw-nav">
        <a href={clientOrigin} className="cgw-logo">
          <div className="cgw-logo-icon">
            <img src="/eprims-logo.png" alt="IntelOptics" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          </div>
          <div>
            <div className="cgw-logo-text">INTELOPTICS <span style={{ color: 'var(--teal)' }}>INSIGHTS</span></div>
            <div className="cgw-logo-sub">Client Portal</div>
          </div>
        </a>
        <div className="cgw-nav-right">
          <div className="cgw-nav-badge">
            <span className="cgw-live-dot" />
            Platform Live
          </div>
          <a href={marketingOrigin} className="cgw-nav-link" target="_blank" rel="noopener noreferrer">eprims.com</a>
          <a href="mailto:contact@eprims.com" className="cgw-nav-link">Support</a>
        </div>
      </nav>

      {/* ── Hero + Login ── */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div className="cgw-hero">

          {/* Left: Welcome content */}
          <div>
            <div className="cgw-hero-badge">
              <span className="cgw-live-dot" />
              IntelOptics Insights — Client Portal
            </div>
            <h1 className="cgw-hero-title">
              Welcome to Your<br />
              <span className="cgw-hero-grad">Intelligence Platform</span>
            </h1>
            <p className="cgw-hero-sub">
              View consultant-managed geopolitical risk intelligence, completed reports, global assets, learning resources, and support from one secure client dashboard. Sign in to get started.
            </p>

            <div className="cgw-feature-pills">
              {['Global Risk Map', 'Geopolitical Deep Dives', 'OSINT Threat Groups', 'Travel Security Briefs', 'SRM Framework', 'HSSE Compliance', 'Crisis Protocols', 'Regional Consultant'].map(f => (
                <span className="cgw-pill" key={f}><span className="cgw-pill-dot" />{f}</span>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <a href={marketingOrigin} className="cgw-eprims-link" target="_blank" rel="noopener noreferrer">
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                Not yet a client? Visit eprims.com
              </a>
              <a href="mailto:contact@eprims.com" className="cgw-eprims-link">
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                contact@eprims.com
              </a>
            </div>
          </div>

          {/* Right: Login card */}
          <div className="cgw-card">
            <div className="cgw-card-header">
              <div className="cgw-card-logo">
                <img src="/eprims-logo.png" alt="IntelOptics"
                  onError={(e) => {
                    const el = e.target as HTMLImageElement;
                    el.style.display = 'none';
                    if (el.parentElement) {
                      el.parentElement.style.background = 'linear-gradient(135deg,#3b82f6,#06b6d4)';
                      el.parentElement.innerHTML = '<span style="color:white;font-weight:900;font-size:1rem;display:flex;align-items:center;justify-content:center;height:100%">IO</span>';
                    }
                  }}
                />
              </div>
              <div className="cgw-card-title">Welcome Back</div>
              <div className="cgw-card-sub">Sign in to IntelOptics Insights</div>
            </div>

            {error && <div className="cgw-alert cgw-alert-err">{error}</div>}
            {info && !error && <div className="cgw-alert cgw-alert-info">{info}</div>}

            {showReset ? (
              <form onSubmit={handlePasswordReset}>
                <p style={{ fontSize: '.82rem', color: 'var(--grey)', marginBottom: '1rem', lineHeight: 1.6 }}>Enter your email address and we'll send you a password reset link.</p>
                <div className="cgw-form-group">
                  <label className="cgw-label">Email Address</label>
                  <input className="cgw-input" type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} required />
                </div>
                {resetSent && <div className="cgw-alert cgw-alert-info">Reset link sent! Check your inbox.</div>}
                <button type="submit" className="cgw-btn" disabled={isLoading || resetSent}>
                  {isLoading ? <span className="cgw-spinner" /> : 'Send Reset Link'}
                </button>
                <div style={{ textAlign: 'center', marginTop: '.75rem' }}>
                  <button type="button" onClick={() => { setShowReset(false); setResetSent(false); setError(null); }} style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer', fontSize: '.8rem', fontWeight: 600 }}>← Back to Sign In</button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="cgw-form-group">
                  <label className="cgw-label">Email Address</label>
                  <input className="cgw-input" type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="username" required />
                </div>
                <div className="cgw-form-group">
                  <label className="cgw-label">Password</label>
                  <input className="cgw-input" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" required />
                  <div className="cgw-forgot">
                    <button type="button" onClick={() => { setShowReset(true); setError(null); }}>Forgot Password?</button>
                  </div>
                </div>
                <button type="submit" className="cgw-btn" disabled={isLoading}>
                  {isLoading ? <span className="cgw-spinner" /> : 'Sign In to IntelOptics →'}
                </button>
              </form>
            )}

            <div className="cgw-card-footer">
              <div style={{ marginBottom: '.5rem' }}>
                🔒 Secured by Firebase Authentication &nbsp;·&nbsp; Billing by <a href="https://paddle.com" target="_blank" rel="noopener noreferrer">Paddle</a>
              </div>
              <div>
                Questions? <a href="mailto:contact@eprims.com">contact@eprims.com</a> &nbsp;·&nbsp; <a href={marketingOrigin}>eprims.com</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr className="cgw-divider-full" />

      {/* ── App Suite Overview ── */}
      <section className="cgw-section">
        <div className="cgw-section-inner">
          <div className="cgw-sec-label">Your Intelligence Coverage</div>
          <h2 className="cgw-sec-title">Six Integrated Capabilities<br />Managed for You</h2>
          <p className="cgw-sec-sub">Your IntelOptics consultant uses these capabilities to prepare work delivered through your client dashboard.</p>
          <div className="cgw-suites-grid">
            {SUITES.map(s => (
              <div className="cgw-suite" key={s.name}>
                <div className="cgw-suite-icon">{s.icon}</div>
                <div className="cgw-suite-name">{s.name}</div>
                <div className="cgw-suite-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="cgw-divider-full" />

      {/* ── Getting Started ── */}
      <section className="cgw-section" style={{ background: 'rgba(15,23,42,0.5)' }}>
        <div className="cgw-section-inner">
          <div className="cgw-sec-label">Getting Started</div>
          <h2 className="cgw-sec-title">Up and Running<br />in Three Steps</h2>
          <p className="cgw-sec-sub">Your IntelOptics Insights subscription is active. Access your dashboard and review consultant-prepared intelligence.</p>
          <div className="cgw-steps">
            {STEPS.map(s => (
              <div className="cgw-step" key={s.num}>
                <div className="cgw-step-num">STEP {s.num}</div>
                <div className="cgw-step-title">{s.title}</div>
                <div className="cgw-step-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="cgw-divider-full" />

      {/* ── Support ── */}
      <section className="cgw-section">
        <div className="cgw-section-inner">
          <div className="cgw-sec-label">Client Support</div>
          <h2 className="cgw-sec-title">We're Here to Help</h2>
          <p className="cgw-sec-sub">Your subscription includes access to our in-app helpdesk and your dedicated regional consultant.</p>
          <div className="cgw-support-grid">
            <div className="cgw-support-card">
              <div className="cgw-support-title">🎧 In-App Helpdesk</div>
              <div className="cgw-support-body">
                Once signed in, access the <strong>Helpdesk</strong> from your dashboard sidebar at any time. Submit support tickets, book regional consultant sessions, and track resolutions — all within the platform.<br /><br />
                <strong>Email:</strong> <a href="mailto:contact@eprims.com">contact@eprims.com</a><br />
                <strong>Phone:</strong> +1 (835) 290-9708<br />
                <strong>Hours:</strong> Mon–Fri, 9 AM–6 PM EST
              </div>
            </div>
            <div className="cgw-support-card">
              <div className="cgw-support-title">🌍 Regional Consultants</div>
              <div className="cgw-support-body">
                Your account includes access to <strong>Eprims Ltd regional consultants</strong> across three coverage areas:<br /><br />
                📍 <strong>Americas & Oceania</strong> — Mexico City, Mexico<br />
                📍 <strong>Africa & Middle East</strong> — Lagos, Nigeria<br />
                📍 <strong>UK, Europe & SE Asia</strong> — London, UK<br /><br />
                Book sessions directly via the in-app helpdesk or at <a href={marketingOrigin}>eprims.com</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="cgw-footer">
        <div className="cgw-footer-inner">
          <div className="cgw-footer-left">
            <strong style={{ color: '#fff' }}>IntelOptics Insights</strong> — Client Portal &nbsp;·&nbsp; Powered by <a href={marketingOrigin} style={{ color: 'var(--blue)', textDecoration: 'none' }}>Eprims Ltd</a><br />
            <span style={{ color: 'var(--lgrey)' }}>© 2026 Eprims Ltd. All rights reserved. &nbsp;|&nbsp; Penn Valley, PA, USA</span>
          </div>
          <div className="cgw-footer-links">
            <a href={marketingOrigin}>eprims.com</a>
            <a href="mailto:contact@eprims.com">Contact</a>
            <a href={clientOrigin + "/privacy-policy"}>Privacy Policy</a>
            <a href={clientOrigin + "/terms-of-service"}>Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClientGateway;
